import { useFieldArray, useFormContext } from "react-hook-form";
import { DragEndEvent } from "@dnd-kit/core";

// Stable field id used by react-hook-form. We use a dedicated `_fieldId` key
// so the source array's `id` field (from the backend) isn't clobbered.
const KEY_NAME = "_fieldId";

type WithFieldId = { [KEY_NAME]: string };

// Combines useFieldArray with @dnd-kit drag handling.
// Returns { fields, ids, onDragEnd, append, remove, move } — ids are stable strings
// suitable for SortableContext items and SortableItem keys.
export const useSortableFieldArray = <TName extends string>(name: TName) => {
  const { control } = useFormContext();
  const { fields, append, remove, move, insert } = useFieldArray({
    control: control as any,
    name: name as any,
    keyName: KEY_NAME,
  });

  const typedFields = fields as unknown as Array<WithFieldId & Record<string, unknown>>;
  const ids = typedFields.map(f => f[KEY_NAME]);

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    move(oldIndex, newIndex);
  };

  return { fields: typedFields, ids, onDragEnd, append, remove, move, insert };
};
